import { Flex, Stat, StatLabel, StatNumber, useColorModeValue, Text, Icon, Skeleton } from '@chakra-ui/react';
import { MdArrowDropUp, MdArrowDropDown, MdErrorOutline } from 'react-icons/md';
import Card from 'components/card/Card';
import CountUp from 'components/animations/CountUp';
import Sparkline from 'components/card/Sparkline';
import { headingFont } from 'theme/tokens';
import { translate } from '../../i18n';
import { useAppSettings } from '../../contexts/AppSettingsContext';

export default function Default(props: {
	startContent?: JSX.Element;
	endContent?: JSX.Element;
	name: string;
	growth?: number;
	growthLabel?: string;
	value: string | number;
	isLoading?: boolean;
	/**
	 * The read behind this number failed and there is no value to show.
	 *
	 * A stat card has no room for `ErrorState`, and until this prop existed
	 * every analytics card answered a refused read the same way: `data` is
	 * `undefined`, so `Boolean(data) ? data.value : isLoading ? '--' : '0'`
	 * fell through to a REAL NUMBER — `0 €`, `0` games, `0%` occupancy. A
	 * quiet week and an unreadable one printed the same thing, on the screen
	 * an operator uses to decide whether the week was quiet.
	 *
	 * When set, the card keeps its geometry (the grid must not reflow) and
	 * says the one true thing instead: the value is unknown. The pill sits
	 * where the growth chip would be and REPLACES it — a growth figure
	 * computed against a number we could not read is the same lie one
	 * derivative along.
	 */
	isError?: boolean;
	sparklineData?: number[];
}) {
	const { startContent, endContent, name, growth, growthLabel, value, isLoading, isError, sparklineData } = props;
	const { aesthetic } = useAppSettings();
	const isClubhouse = aesthetic === 'clubhouse';
	const textColor = useColorModeValue('secondaryGray.900', 'white');
	const textColorSecondary = 'secondaryGray.600';

	const hasGrowth = !isError && growth !== undefined && growth !== null && isFinite(growth);
	const isPositive = hasGrowth && growth >= 0;
	const growthColor = isPositive ? 'success.500' : 'danger.500';
	const growthBg = useColorModeValue(
		isPositive ? 'success.50' : 'danger.50',
		'whiteAlpha.50'
	);
	// Declared unconditionally: hooks cannot sit behind `isError`.
	const errorBg = useColorModeValue('danger.50', 'whiteAlpha.50');
	const GrowthIcon = isPositive ? MdArrowDropUp : MdArrowDropDown;

	const numericValue = typeof value === 'number' ? value : parseFloat(value);
	const isNumeric = !isNaN(numericValue) && typeof value === 'number';

	if (isLoading) {
		return (
			<Card py='15px'>
				<Flex my='auto' h='100%' align='center' justify='center'>
					{startContent && <Skeleton borderRadius='50%' w='40px' h='40px' mr='18px' />}
					<Flex direction='column' flex={1}>
						<Skeleton height='14px' width='60%' mb='8px' borderRadius='8px' />
						<Skeleton height='28px' width='40%' borderRadius='8px' />
					</Flex>
				</Flex>
			</Card>
		);
	}

	return (
		<Card py='15px' elevation='raised'>
			<Flex my='auto' h='100%' align={{ base: 'center', xl: 'start' }} justify={{ base: 'center', xl: 'center' }}>
				{startContent}

				<Stat my='auto' ms={startContent ? '18px' : '0px'}>
					<StatLabel
						lineHeight='100%'
						color={textColorSecondary}
						fontSize={{ base: 'sm' }}
						{...(isClubhouse ? {
							fontFamily: '"Urbanist", sans-serif',
							fontWeight: '700',
							textTransform: 'uppercase' as const,
							fontSize: '11px',
							letterSpacing: '0.06em',
						} : {})}
					>
						{name}
					</StatLabel>
					<StatNumber
						color={textColor}
						fontFamily={isClubhouse ? 'var(--heading-font)' : headingFont}
						letterSpacing='-0.03em'
						fontSize={{ base: isClubhouse ? '3xl' : '2xl' }}
						fontWeight={isClubhouse ? '800' : undefined}
						sx={isClubhouse ? { fontFeatureSettings: '"tnum" 1' } : undefined}
					>
						{isNumeric ? <CountUp end={numericValue} /> : value}
					</StatNumber>
					{isError ? (
						<Flex
							align='center'
							mt='4px'
							bg={errorBg}
							borderRadius='full'
							px='6px'
							py='2px'
							w='fit-content'
							role='status'
						>
							<Icon as={MdErrorOutline} color='danger.500' w='14px' h='14px' />
							<Text color='danger.500' fontSize='xs' fontWeight='600' ms='4px'>
								{translate('common.unknown')}
							</Text>
						</Flex>
					) : hasGrowth ? (
						<Flex
							align='center'
							mt='4px'
							bg={growthBg}
							borderRadius='full'
							px='6px'
							py='2px'
							w='fit-content'
						>
							<Icon as={GrowthIcon} color={growthColor} w='16px' h='16px' />
							<Text
								color={growthColor}
								fontSize='xs'
								fontWeight={isClubhouse ? '500' : '600'}
								fontFamily={isClubhouse ? 'var(--mono-font)' : undefined}
							>
								{isPositive ? '+' : ''}{growth.toFixed(1)}%
							</Text>
							{growthLabel ? (
								<Text color='secondaryGray.600' fontSize='xs' fontWeight='400' ms='4px'>
									{growthLabel}
								</Text>
							) : null}
						</Flex>
					) : null}
				</Stat>
				<Flex ms='auto' w='max-content' align='center'>
					{sparklineData && sparklineData.length >= 2 && !endContent && (
						<Sparkline data={sparklineData} />
					)}
					{endContent}
				</Flex>
			</Flex>
		</Card>
	);
}
